import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { Loader } from "@circle-react-uikit/Loader";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor/BlockEditorContext";
import { defer, isArray } from "lodash";
import { forwardRef, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { useKeyboardHandler } from "../hooks/useKeyboardHandler";
import { getMentionScope } from "./helper";
import { useMentions } from "./hooks/useMentions";
import { MentionItem } from "./MentionItem";
import { MentionItem as MentionItemCompass } from "./MentionItemCompass";

interface MentionListProps {
  command: (item: any) => void;
  query: string;
}

export const MENTION_LIST_ID = "mentionList";

export const MentionList = forwardRef(
  ({ query, command }: MentionListProps, ref) => {
    const tiptapEditorContext = useTipTapEditorContext();
    const tiptapBlockEditorContext = useBlockEditorContext();
    const { isCircleV4 = false } = useCircleV4();
    const {
      addToLocalSgidToObjectMap,
      spaceId,
      chatProps,
      type: editorType,
      isUpdate,
    } = tiptapEditorContext || tiptapBlockEditorContext;

    const { isThread, isDirectMessaging, disableGroupMentions } =
      chatProps || {};
    const { setPopupsVisible } = useInternalEditorState();

    const { items, fetchNextPage, hasNextPage, isLoading } = useMentions({
      query,
      spaceId: spaceId,
      scope: getMentionScope({
        spaceId,
        isDirectMessaging,
        isThread,
        editorType,
        isUpdate,
        disableGroupMentions,
      }),
    });

    const hasItems = isArray(items) && items.length > 0;

    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        addToLocalSgidToObjectMap({ sgid: item.sgid, object: item });
        command(item);
      }
    };

    useEffect(() => setSelectedIndex(0), [items]);

    useKeyboardHandler({
      selectedIndex,
      setSelectedIndex,
      ref,
      items,
      selectItem,
    });

    useEffect(() => {
      if (isLoading || !hasItems) {
        setPopupsVisible(false);
      } else {
        setPopupsVisible(true);
      }

      return () => setPopupsVisible(false);
    }, [hasItems, setPopupsVisible, isLoading]);

    if (!hasItems) {
      return (
        <div
          className={classNames(
            "h-[50vh] max-w-[320px] bg-transparent",
            isCircleV4 ? "min-w-[220px]" : "min-w-[250px]",
          )}
        />
      );
    }

    const MentionItemComponent = isCircleV4 ? MentionItemCompass : MentionItem;
    return (
      <div
        className={classNames(
          "bg-primary border-primary relative flex max-h-[50vh] min-w-[250px] max-w-[320px] flex-col space-y-0.5 overflow-y-auto border text-sm shadow-md",
          isCircleV4
            ? "p-1 rounded-md min-w-[220px]"
            : "py-1.5 rounded-sm min-w-[250px]",
        )}
        id={MENTION_LIST_ID}
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          dataLength={items.length}
          loader={<Loader center />}
          scrollableTarget="mentionList"
        >
          {items.map((member, index) => (
            <MentionItemComponent
              key={member.id}
              member={member}
              handleClick={() => {
                selectItem(index);
                defer(() => setPopupsVisible(false));
              }}
              isSelected={index === selectedIndex}
            />
          ))}
        </InfiniteScroll>
      </div>
    );
  },
);

MentionList.displayName = "MentionList";
